import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
import {
  Button,
  Chip,
  DottedText,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  Avatar,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface PlaybookCardProps {
  title: string;
  subTitle: string;
  isAccepted: boolean;
  applicableTo: string[];
  redirectAction?: () => void;
  buttonAction: () => void;
  acceptedBy: string;
  width?: string;
  height?: string;
  padding?: string;
  mainGridStyles?: CSSObject;
  isDefaultAccepted?: boolean;
}
const PlaybookCard: React.FC<PlaybookCardProps> = ({
  title,
  subTitle,
  isAccepted,
  applicableTo,
  acceptedBy,
  redirectAction,
  buttonAction,
  width,
  height,
  padding,
  mainGridStyles,
  isDefaultAccepted,
}) => {
  const useStyles = makeStyles()({
    root: {
      padding: padding ?? '20px 24px 24px 20px',
      width: width,
      height: height,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05);',
      borderRadius: '8px',
      background: patientAccessTheme.palette.status.white,
      border: '1px solid' + patientAccessTheme.palette.grey[300],
      position: 'relative',

      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      ...mainGridStyles,
    },
    titleSubtitleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    chip: {
      height: '25px',
      fontSize: '12px',
      width: '100px',
      borderRadius: '4px',
    },
    separator: {
      width: '100%',
      height: '1px',
      backgroundColor: patientAccessTheme.palette.grey[300],
      border: 'none',
    },
    applicableContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  });
  const getChip = () => {
    return isAccepted ? (
      <Chip
        className={classes.chip}
        textColor={patientAccessTheme.palette.AIQChips?.textColor2}
        backgroundColor={patientAccessTheme.palette.AIQChips?.background2}
        border="0.1px solid rgba(0, 130, 114, 0.5)"
        label={
          <Typography
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: patientAccessTheme.palette.AIQChips?.textColor2,
            }}
          >
            Accepted
          </Typography>
        }
      />
    ) : (
      <Chip
        className={classes.chip}
        label={
          <Typography
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: patientAccessTheme.palette.text.mediumemphasis,
            }}
          >
            Yet to accept
          </Typography>
        }
      />
    );
  };
  const getButton = () => {
    return isAccepted ? (
      <div onClick={buttonAction}>
        <DottedText
          text={'View Playbook'}
          typographyStyles={{
            fontSize: '14px',
            fontWeight: 600,
          }}
        />
      </div>
    ) : (
      <Button
        color="primary"
        height="36px"
        width="200px"
        label="Accept this Playbook"
        onClick={buttonAction}
      />
    );
  };
  const extractInitials = (name: string) => {
    const parts = name.split(' ');

    const initials = parts.map((part) => part?.[0]?.toUpperCase()).join('');
    return initials;
  };
  const { classes } = useStyles();
  return (
    <Grid container className={classes.root}>
      <Grid className={classes.titleSubtitleContainer}>
        <Grid style={{ width: '70%' }}>
          <Grid
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}
          >
            <div onClick={isDefaultAccepted ? buttonAction : redirectAction}>
              <DottedText
                text={title}
                typographyStyles={{
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              />
            </div>
            {getChip()}
          </Grid>
          <Typography
            fontSize="14px"
            fontType="regular"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            {subTitle}
          </Typography>
        </Grid>
        {getButton()}
      </Grid>
      <hr className={classes.separator} />
      <Grid className={classes.applicableContainer}>
        <Grid
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
          }}
        >
          <Typography
            fontSize="12px"
            fontType="regular"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            Applicable to:
          </Typography>

          {applicableTo.slice(0, 3).map((label, index) => (
            <Chip
              height="25px"
              border="0.5px solid #E5E9F0"
              backgroundColor="#FFFFFF"
              label={
                <Tooltip
                  disableFocusListener
                  placement="top-end"
                  title={<Typography fontSize="12px">{label}</Typography>}
                  arrow={true}
                  key={index}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: patientAccessTheme.palette.text.mediumemphasis,
                    }}
                  >
                    {label}
                  </div>
                </Tooltip>
              }
            />
          ))}
        </Grid>
        {isDefaultAccepted ? (
          <Grid style={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon
              style={{
                height: '16px',
                width: '16px',
                marginRight: '0.5rem',
                fill: patientAccessTheme.palette.text.lowemphasis,
              }}
            />
            <Typography
              fontSize="12px"
              fontType="regular"
              textColor={patientAccessTheme.palette.text.lowemphasis}
            >
              Accepted by default
            </Typography>
          </Grid>
        ) : (
          isAccepted && (
            <Grid
              style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
              }}
            >
              <Typography
                fontSize="12px"
                fontType="regular"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                Accepted by:
              </Typography>
              <Avatar
                avatarText={
                  <Typography
                    textColor="#FDFAFA"
                    fontType="semiBold"
                    fontSize="12px"
                  >
                    {extractInitials(acceptedBy)}
                  </Typography>
                }
                height="32px"
                width="32px"
                backgroundColor={patientAccessTheme.palette.primary[400]}
                alt={''}
                style={{ margin: '0px 10px 0px 0px' }}
              />
            </Grid>
          )
        )}
      </Grid>
    </Grid>
  );
};

export default PlaybookCard;
